import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

const SIZE = 104;
const CENTER = SIZE / 2;
const RADIUS = 46;
const STROKE = 6;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
// Visual gap between segments, in px along the arc.
const SEGMENT_GAP = 4;

// `reviewed` sits between the other two: filled, so the ring agrees with the row
// label that the area is resolved, but muted rather than primary, so a ring of
// advisor attestations never looks identical to a ring of verified work.
const SEGMENT_STROKE = {
  to_do: 'var(--succession-border, #E5E7EB)',
  done: 'var(--succession-primary, #5489B6)',
  reviewed: 'var(--succession-text-secondary, #6B7280)',
};

/**
 * ReadinessRing
 *
 * One arc per scored category rather than a single percentage sweep — the same
 * three states the category rows use, so the ring reads as a legend for the
 * breakdown beside it rather than as a separate number.
 *
 * Shared by the LaunchPad score card and the assessment's closing screen so the
 * advisor sees the identical mark in both places.
 *
 * `rows` is [{ key, status }] where status is to_do | done | reviewed — segment
 * count follows whatever the caller shows, so a horizon that gates a category
 * out drops its arc too and the ring can never claim an area the breakdown does
 * not list.
 */
const ReadinessRing = ({ rows, overall, className }) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  if (!rows || rows.length === 0) return null;

  // Reviewed counts as resolved: an override that moved the label but left the
  // ring stuck at "3 areas need attention" would read as broken, which is the
  // complaint this whole change answers.
  const readyCount = rows.filter((r) => r.status !== 'to_do').length;
  const segmentLength = CIRCUMFERENCE / rows.length;
  const segmentDash = Math.max(segmentLength - SEGMENT_GAP, 0);

  return (
    <div
      className={`relative flex-shrink-0 ${className}`}
      style={{ width: SIZE, height: SIZE }}
      title={overall == null ? undefined : `${overall}% ready overall`}>
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="-rotate-90"
        role="img"
        aria-label={`${readyCount} of ${rows.length} areas ready${overall == null ? '' : `, ${overall}% overall`}`}>
        {/* Explicit strokes rather than currentColor: `text-succession-bg-secondary`
            is not a configured Tailwind text utility, so it was a no-op and
            currentColor inherited the near-black body text. */}
        {rows.map((row, i) => (
          <circle
            key={row.key}
            cx={CENTER}
            cy={CENTER}
            r={RADIUS}
            fill="none"
            strokeWidth={STROKE}
            stroke={SEGMENT_STROKE[row.status] || SEGMENT_STROKE.to_do}
            strokeDasharray={mounted
              ? `${segmentDash} ${CIRCUMFERENCE - segmentDash}`
              : `0 ${CIRCUMFERENCE}`}
            strokeDashoffset={-i * segmentLength}
            style={{
              transition: 'stroke-dasharray 700ms cubic-bezier(0.22, 1, 0.36, 1)',
              transitionDelay: `${i * 70}ms`,
            }}
          />
        ))}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {/* Standard scale only — no `text-[Npx]`. The succession layout loads
            Tailwind v2 from CDN, which predates JIT, so arbitrary values
            generate no CSS and the element silently inherits 16px. */}
        <span className="text-4xl font-bold text-succession-text-primary leading-none tabular-nums">
          {readyCount}
          <span className="text-lg text-succession-text-secondary">/{rows.length}</span>
        </span>
        {/* One short word: "Areas ready" needs ~85px and the ring leaves ~80px
            inside, so it crowded the stroke. */}
        <span className="text-xs font-medium uppercase tracking-wider text-succession-text-secondary mt-1.5">Areas</span>
      </div>
    </div>
  );
};

ReadinessRing.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    status: PropTypes.oneOf(['to_do', 'done', 'reviewed']),
  })),
  // Kept on the title and aria-label so it stays available without competing
  // for the centre of the ring.
  overall: PropTypes.number,
  className: PropTypes.string,
};

ReadinessRing.defaultProps = { rows: [], overall: null, className: '' };

export default ReadinessRing;
